import { useEffect, useId, useState } from 'react'

import { getLocalTimeZone, parseDate, today } from '@internationalized/date'
import {
  type DatePickerProps,
  type DateValue,
  Button,
  DatePicker,
  Dialog,
  Group,
  Popover,
  I18nProvider,
} from 'react-aria-components'
import type { Control, FieldValues, UseControllerProps } from 'react-hook-form'

import { Icon } from '@/components/icon'
import { Calendar } from '@/components/ui/calendar-rac'
import { DateInput } from '@/components/ui/datefield-rac'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

type IDatePickerFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = Omit<UseControllerProps<TFieldValues>, 'control'> & {
  control?: Control<TFieldValues, TContext, TTransformedValues>
  label: string
  onChangeDate?: (date: string) => void
  minYear?: number
  maxYear?: number
} & DatePickerProps<DateValue>

const isValidDate = (
  value: DateValue | null,
  minYear: number = 1800,
  maxYear: number = new Date().getFullYear() + 70,
): boolean => {
  if (!value) return false
  try {
    const year = value.year
    const month = value.month
    const day = value.day

    if (!year || year < minYear || year > maxYear) return false
    if (!month || month < 1 || month > 12) return false
    if (!day || day < 1 || day > 31) return false

    return true
  } catch {
    return false
  }
}

type DatePickerContentProps = {
  field: {
    value: string | null
    onChange: (value: string | null) => void
    onBlur: () => void
  }
  label: string
  id: string
  onChangeDate?: (date: string) => void
  minYear: number
  maxYear: number
  datePickerProps: DatePickerProps<DateValue>
}

const DatePickerContent = ({
  field,
  label,
  id,
  onChangeDate,
  minYear,
  maxYear,
  datePickerProps,
}: DatePickerContentProps) => {
  const [internalValue, setInternalValue] = useState<DateValue | null>(field.value ? parseDate(field.value) : null)

  useEffect(() => {
    if (field.value) {
      setInternalValue(parseDate(field.value))
      return
    }
    setInternalValue(null)
  }, [field.value])

  const handleInternalChange = (v: DateValue | null) => {
    setInternalValue(v)
  }

  const handleBlur = () => {
    if (isValidDate(internalValue, minYear, maxYear) && internalValue) {
      field.onChange(internalValue.toString())
      onChangeDate?.(internalValue.toString())
    } else if (!internalValue) {
      field.onChange(null)
    }
    field.onBlur()
  }

  const handleCalendarChange = (v: DateValue | null) => {
    if (isValidDate(v, minYear, maxYear) && v) {
      setInternalValue(v)
      field.onChange(v.toString())
      onChangeDate?.(v.toString())
      field.onBlur()
    }
  }

  const getEffectiveMinValue = () => {
    const proposedMin = datePickerProps.minValue ?? today(getLocalTimeZone())
    if (!field.value) return proposedMin

    const parsedValue = parseDate(field.value)
    return parsedValue.compare(proposedMin) < 0 ? parsedValue : proposedMin
  }

  return (
    <FormItem>
      <FormLabel id={id}>{label}</FormLabel>
      <I18nProvider locale="pt-BR">
        <DatePicker
          {...field}
          {...datePickerProps}
          minValue={getEffectiveMinValue()}
          aria-labelledby={id}
          className="*:not-first:mt-2"
          value={internalValue}
          data-testid="date-picker"
          onBlur={handleBlur}
          onChange={handleInternalChange}
        >
          <div className="group flex">
            <Group className="w-full">
              <FormControl>
                <DateInput
                  className={`aria-invalid:border-destructive h-11 pe-9 ${internalValue ? 'bg-slate-100 text-neutral-800 aria-invalid:bg-neutral-100' : ''}`}
                />
              </FormControl>
            </Group>
            <Button
              isDisabled={datePickerProps.isDisabled}
              aria-disabled={datePickerProps.isDisabled}
              className="hover:text-primary data-focus-visible:border-ring data-focus-visible:ring-ring/50 z-10 -ms-9 -me-px flex w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none data-disabled:cursor-not-allowed data-focus-visible:ring-[3px]"
            >
              <Icon
                aria-disabled={datePickerProps.isDisabled}
                name="calendar-range"
                size={16}
                className="aria-[invalid=true]:text-destructive aria-[disabled=true]:text-gray-400"
              />
            </Button>
          </div>
          <Popover
            className="bg-foreground text-popover-foreground data-entering:animate-in data-exiting:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2 z-50 rounded-lg border shadow-lg outline-hidden"
            offset={4}
          >
            <Dialog className="max-h-[inherit] overflow-auto p-2">
              <Calendar
                isDisabled={datePickerProps.isDisabled}
                onChange={handleCalendarChange}
                value={field.value ? parseDate(field.value) : undefined}
                minValue={getEffectiveMinValue()}
                maxValue={datePickerProps.maxValue}
              />
            </Dialog>
          </Popover>
        </DatePicker>
      </I18nProvider>
      <FormMessage />
    </FormItem>
  )
}

export const DatePickerField = <
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  control,
  label,
  onChangeDate,
  minYear = 1800,
  maxYear = new Date().getFullYear() + 50,
  ...props
}: IDatePickerFieldProps<TFieldValues, TContext, TTransformedValues>) => {
  const id = useId()

  return (
    <FormField
      control={control}
      name={props.name}
      render={({ field }) => (
        <DatePickerContent
          field={field}
          label={label}
          id={id}
          onChangeDate={onChangeDate}
          minYear={minYear}
          maxYear={maxYear}
          datePickerProps={props}
        />
      )}
    />
  )
}
